import { Button } from './ui/button';
import { FilterX, Eye, Grid3x3, Search } from 'lucide-react';
import type { BrowseMode } from '@/hooks/useUnitFilters';

interface EmptyFilterStateProps {
  browseMode: BrowseMode;
  activeFilters: string[];
  onClearFilters: () => void;
  onViewAllUnits: () => void;
}

export const EmptyFilterState = ({
  browseMode,
  activeFilters,
  onClearFilters,
  onViewAllUnits,
}: EmptyFilterStateProps) => {
  const getContent = () => {
    switch (browseMode) {
      case 'featured':
        return {
          icon: Eye,
          title: 'No Configurations Match',
          description: 'The selected category has no featured configurations available.',
          suggestions: [
            { label: 'View All Categories', action: onClearFilters },
            { label: 'Browse All Units', action: onViewAllUnits },
          ],
        };
      case 'configurations':
        return {
          icon: Grid3x3,
          title: 'No Configurations Found',
          description: activeFilters.length > 0
            ? 'No configurations match your current filter combination.'
            : 'No configurations available at this time.',
          suggestions: activeFilters.length > 0
            ? [
                { label: 'Clear All Filters', action: onClearFilters },
                { label: 'Browse All Units', action: onViewAllUnits },
              ]
            : [{ label: 'Browse All Units', action: onViewAllUnits }],
        };
      case 'all-units':
        return {
          icon: Search,
          title: 'No Units Found',
          description: activeFilters.length > 0
            ? 'No units match your current filters. Try adjusting your criteria.'
            : 'No units available at this time.',
          suggestions: activeFilters.length > 0
            ? [
                { label: 'Clear Filters', action: onClearFilters },
                { label: 'View Configurations', action: () => window.location.href = '/residences?tab=configurations' },
              ]
            : [],
        };
      default:
        return {
          icon: Search,
          title: 'No Results',
          description: 'No units match your criteria.',
          suggestions: [{ label: 'Clear Filters', action: onClearFilters }],
        };
    }
  };

  const content = getContent();
  const Icon = content.icon;

  return (
    <div className="min-h-[50vh] flex items-center justify-center py-12">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
            <Icon className="w-10 h-10 text-muted-foreground opacity-50" />
          </div>
        </div>
        
        <h3 className="font-display text-2xl lg:text-3xl font-bold mb-3 text-foreground">
          {content.title}
        </h3>
        
        <p className="text-muted-foreground text-base mb-8 leading-relaxed">
          {content.description}
        </p>

        {activeFilters.length > 0 && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-3">Active filters:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {activeFilters.map((filter, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-muted rounded-full text-xs font-medium"
                >
                  {filter}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {content.suggestions.map((suggestion, index) => (
            <Button
              key={index}
              variant={index === 0 ? 'default' : 'outline'}
              onClick={suggestion.action}
              className={index === 0 ? 'bg-brand-teal text-white hover:bg-brand-navy' : ''}
            >
              {index === 0 && <FilterX className="w-4 h-4 mr-2" />}
              {suggestion.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
